import type { SVGAttributes } from "react";
import { cn } from "../lib/utils";

/** The participant hue, so a mark reads the same wherever it is drawn. */
const PARTICIPANT_MARK_HUE = "var(--sqk-participant)";

const ROWS = 6;
/** Unique columns per row: outer, inner and centre; the outer and inner ones are mirrored. */
const HALF_COLUMNS = 3;
/** Bound on re-draws; a random pattern meets the shape rule about 42% of the time. */
const MAX_DRAWS = 64;
/** Used only if no draw meets the rule (practically never): a full-width, full-height mark. */
const FALLBACK_PATTERN = 0b011_101_000_111_010_011;

function fnv1a(text: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

/** Bit `row * 3 + column` lights the cell in that row and half-column (and its mirror). */
const lit = (pattern: number, row: number, column: number) => ((pattern >>> (row * HALF_COLUMNS + column)) & 1) === 1;

/**
 * The shape every mark keeps, so marks read as variations of one family rather than as noise:
 * - 13–18 of the 30 cells lit (mirrored cells count twice);
 * - the top and bottom rows lit, and at most one empty row;
 * - full width: the outer columns lit in at least two rows;
 * - the centre column lit in at least one row, so the halves join.
 * About 109,000 of the 262,144 patterns qualify.
 */
export function participantMarkFits(pattern: number): boolean {
  let cells = 0;
  let emptyRows = 0;
  let outerRows = 0;
  let centreRows = 0;
  for (let row = 0; row < ROWS; row += 1) {
    const outer = lit(pattern, row, 0);
    const inner = lit(pattern, row, 1);
    const centre = lit(pattern, row, 2);
    cells += (outer ? 2 : 0) + (inner ? 2 : 0) + (centre ? 1 : 0);
    if (!outer && !inner && !centre) {
      if (row === 0 || row === ROWS - 1) return false;
      emptyRows += 1;
    }
    if (outer) outerRows += 1;
    if (centre) centreRows += 1;
  }
  return cells >= 13 && cells <= 18 && emptyRows <= 1 && outerRows >= 2 && centreRows >= 1;
}

/** The identity's 18-bit pattern: its first draw that fits the shape rule. */
export function participantMarkPattern(identity: string): number {
  const normalized = identity.trim().toLowerCase() || "player";
  for (let draw = 0; draw < MAX_DRAWS; draw += 1) {
    // Draw 0 hashes the identity alone, so marks that already fit keep their shape.
    const pattern = fnv1a(draw === 0 ? normalized : `${normalized}#${draw}`) & 0x3ffff;
    if (participantMarkFits(pattern)) return pattern;
  }
  return FALLBACK_PATTERN;
}

/**
 * Deterministic mark for a participant identity: the same identity always draws the same sigil, and
 * two identities practically never share one. Cells are mirrored left to right, so the mark reads as
 * one glyph, and every mark keeps the family shape of {@link participantMarkFits}.
 */
export function participantMarkPath(identity: string): string {
  const pattern = participantMarkPattern(identity);
  let path = "";
  for (let row = 0; row < ROWS; row += 1) {
    for (let column = 0; column < HALF_COLUMNS; column += 1) {
      if (!lit(pattern, row, column)) continue;
      const y = row * 2;
      const left = 1 + column * 2;
      const right = 9 - column * 2;
      path += `M${left} ${y}h2v2h-2Z`;
      if (right !== left) path += `M${right} ${y}h2v2h-2Z`;
    }
  }
  return path;
}

export interface ParticipantMarkProps extends Omit<SVGAttributes<SVGSVGElement>, "children"> {
  /** The identity the mark is derived from, e.g. a wallet address. */
  readonly identity: string;
  readonly label?: string;
}

/**
 * The generative participant mark at its default 12px; callers scale it through `style` or a class
 * of their own. Pass `aria-hidden` where surrounding text already names the participant.
 */
export function ParticipantMark({
  identity,
  label,
  className,
  style,
  ...props
}: ParticipantMarkProps) {
  return (
    <svg
      {...props}
      aria-label={label ?? "Participant mark"}
      className={cn("sqk-participant-mark", className)}
      role="img"
      style={{ color: PARTICIPANT_MARK_HUE, ...style }}
      viewBox="0 0 12 12"
    >
      <path d={participantMarkPath(identity)} fill="currentColor" />
    </svg>
  );
}
